import { Component } from 'vue'

import { WidgetConfig } from '@/types/dashboard'
import { AiParameterValueJson } from '@/types/widgetParameters'
import { validatePromptsConfig } from '@/composables/usePromptsValidation'
import { getTemplateParameterPrettyName } from '@/components/RedesignComponents/Widgets/widget.helper'
import ENUM from '@/enum/parameters'
import {
    SUMMARY_CDR_TEMPLATE_PARAMETER_NAME,
    SYSTEM_PROMPT_PARAMETER_NAME
} from '@/enum/widgetTemplateConfigs'

import SetupAiTemplateData
    from '@/components/RedesignComponents/Widgets/Parameters/SetupAiTemplateData.vue'
import SetupAiTemplateDataSummary
    from '@/components/RedesignComponents/Widgets/Parameters/SetupAiTemplateDataSummary.vue'
import SetupSystemPrompt
    from '@/components/RedesignComponents/Widgets/Parameters/SetupSystemPrompt.vue'
import SetupSystemPromptSummary
    from '@/components/RedesignComponents/Widgets/Parameters/SetupSystemPromptSummary.vue'

export interface IWidgetParameterWizardEntry {
    label: string
    sublabel?: string
    order: number
    component: Component
    summaryComponent: Component
    validate: (config: WidgetConfig) => boolean
    optional?: boolean
}

function hasTemplateData (config: WidgetConfig): boolean {
    const data = (config.WidgetParameterValueJson as AiParameterValueJson | undefined)?.data
    if (Array.isArray(data)) {
        return !!data.length
    }
    return !!String(data ?? '').trim()
}

export const widgetParameterWizardRegistry: Record<string, IWidgetParameterWizardEntry> = {
    [SYSTEM_PROMPT_PARAMETER_NAME]: {
        label: 'Analysis',
        order: 1,
        component: SetupSystemPrompt,
        summaryComponent: SetupSystemPromptSummary,
        validate: validatePromptsConfig
    },
    [SUMMARY_CDR_TEMPLATE_PARAMETER_NAME]: {
        label: 'Data fields',
        order: 2,
        component: SetupAiTemplateData,
        summaryComponent: SetupAiTemplateDataSummary,
        validate: hasTemplateData
    }
}

function wizardParameterNameOf (config: Partial<WidgetConfig>): string {
    if (config.ParameterName && widgetParameterWizardRegistry[config.ParameterName]) {
        return config.ParameterName
    }

    return ENUM.PROMPTS_MODAL_TABS.includes(config.ParameterType ?? -1)
        ? SYSTEM_PROMPT_PARAMETER_NAME
        : ''
}

export function getWizardParameterEntry (
    config: Partial<WidgetConfig>
): IWidgetParameterWizardEntry | undefined {
    return widgetParameterWizardRegistry[wizardParameterNameOf(config)]
}

export function isWizardParameter (config: Partial<WidgetConfig>): boolean {
    return !!getWizardParameterEntry(config)
}

export function getWizardParameterLabel (config: WidgetConfig): string {
    return String(
        getTemplateParameterPrettyName(config)
            || getWizardParameterEntry(config)?.label
            || config.ParameterName
            || ''
    )
}

export function getWizardParameters (configs: Array<WidgetConfig>): Array<WidgetConfig> {
    return configs
        .filter(isWizardParameter)
        .sort((a, b) => (getWizardParameterEntry(a)?.order ?? 0) - (getWizardParameterEntry(b)?.order ?? 0))
}

export function isValidWizardParameter (config: WidgetConfig): boolean {
    return getWizardParameterEntry(config)?.validate(config) ?? true
}

export function findInvalidWizardParameterIndex (configs: Array<WidgetConfig>): number {
    return configs.findIndex(config => !isValidWizardParameter(config))
}
